<template>
  <table class="print-layout">
    <thead class="print-header">
      <tr>
        <td>
          <!--
            Print header — a single row containing the report
            context on the left ("Organisation: Report · Date
            Range") and the TMC logo on the right. Reads top-to-
            bottom on the printout as: which customer, which
            dashboard, which date range — everything an operator
            needs to identify a printed report at a glance.
            The bottom margin footer ("Report powered by Travel
            Analytics" + page numbers) lives in @page CSS below.
          -->
          <div class="tw-flex tw-justify-between tw-items-center">
            <div class="print-report-meta">
              {{ reportContextText }}
            </div>
            <ClientLogo />
          </div>
        </td>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>
          <slot />
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script setup lang="ts">
// This component adds the customer/TMC header to printed pages and
// injects a print-only @page margin footer carrying the report name
// (bottom-left) and page numbers (bottom-right).
import { computed, watch, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { format } from 'date-fns'

import ClientLogo from '@/components/ClientLogo.vue'
import { useHeaderStore } from '@/stores/header'
import { printContextReportName } from '@/utils/printContext'

defineOptions({ name: 'PrintLayout' })

const route = useRoute()
const headerStore = useHeaderStore()
const {
  filterDataGetter: filters,
  selectedOrganisationGetter: selectedOrganisation,
} = storeToRefs(headerStore)

// Report name with override:
//   1. printContextReportName — set by PagedTable's print logic
//      when the user prints a specific tab / modal table; the
//      override is e.g. "Business Units (Cost Centres)" or a
//      modal-supplied "Top Performers - Business Units …"
//   2. route.name — the dashboard label (Summary, Airfare, etc.),
//      used when the print covers the whole page
const reportName = computed<string>(() => {
  if (printContextReportName.value) return printContextReportName.value
  const name = route.name
  return typeof name === 'string' ? name : ''
})

// Customer name — prefer the resolved Organisation record's
// name, but fall back to the raw filter node's `label`. The
// `selectedOrganisationGetter` only resolves when the user picked
// a node that's the root of a known Organisation; for sub-entity
// selections (or while the tree store is still loading), the node
// label is the safest source of "who is this report about".
const customerName = computed<string>(() => {
  const org: any = selectedOrganisation.value
  if (org?.name) return org.name
  const node: any = (filters.value as any)?.node
  return node?.label || ''
})

const dateRangeText = computed<string>(() => {
  const dateRange: any = (filters.value as any)?.dateRange
  if (dateRange?.value?.length === 2) {
    const [start, end] = dateRange.value
    try {
      return `${format(new Date(start), 'dd MMM yyyy')} – ${format(new Date(end), 'dd MMM yyyy')}`
    } catch {
      // Defensive fall-through to shortcut label
    }
  }
  return dateRange?.shortcut || ''
})

// Single-line print header: "{Organisation}: {Report} · {Date Range}".
// The colon binds the customer to the report (so it reads "Acme Corp's
// Summary report") while the middle-dot separates the supplementary
// date range. Each piece is independently optional — falsy values are
// stripped so we never print a dangling "·" or stray colon.
const reportContextText = computed<string>(() => {
  const customer = customerName.value
  const report = reportName.value
  const headPieces: string[] = []
  if (customer && report) headPieces.push(`${customer}: ${report}`)
  else if (customer) headPieces.push(customer)
  else if (report) headPieces.push(report)
  if (dateRangeText.value) headPieces.push(dateRangeText.value)
  return headPieces.join('  ·  ')
})

// --- @page margin footer ---------------------------------------
//
// "{ReportName} powered by Travel Analytics"  on the left, and
// "Page X of Y" on the right — printed in the bottom margin of
// every page via CSS Paged Media (@page margin boxes).
//
// @page margin boxes don't accept Vue interpolation directly —
// `content:` resolves at print time, not at template-compile time.
// We inject a dedicated <style> tag into <head> with the current
// report name baked into the @page rule, and update it whenever
// the route name changes.

const STYLE_ID = 'print-layout-page-meta'

// Defensive escape for the value going into a CSS `content: "…"`
// string. Avoids breaking the rule on names containing quotes.
function escapeForCssContent (value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}

function syncPrintMetaStylesheet (): void {
  let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null
  if (!el) {
    el = document.createElement('style')
    el.id = STYLE_ID
    document.head.appendChild(el)
  }
  const leftText = reportName.value
    ? `${escapeForCssContent(reportName.value)} powered by Travel Analytics`
    : 'Powered by Travel Analytics'
  el.textContent = `
    @media print {
      @page {
        margin: 1.5cm 1cm 2cm 1cm;
        @bottom-left {
          content: "${leftText}";
          font-size: 10px;
          color: #6b7280;
        }
        @bottom-right {
          content: "Page " counter(page) " of " counter(pages);
          font-size: 10px;
          color: #6b7280;
        }
      }
    }
  `
}

// Keep the @page CSS in lock-step with the active route.
// `flush: 'sync'` so the stylesheet is up to date by the time the
// browser snapshots for print, even if the user clicks Print
// immediately after navigation.
watch(reportName, syncPrintMetaStylesheet, { immediate: true, flush: 'sync' })

onUnmounted(() => {
  document.getElementById(STYLE_ID)?.remove()
})
</script>

<style lang="scss" scoped>
.print-report-meta {
  font-size: 14px;
  font-weight: 500;
  color: #111827;
  letter-spacing: 0.01em;
}
</style>
